import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { sendPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    const { error } = await sendPasswordReset(email);

    if (error) {
      setError(error);
    } else {
      setSuccess('Password reset link sent! Check your email.');
      setSubmitted(true);
      setEmail('');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-warm-50 dark:bg-gray-950 flex flex-col items-center justify-center px-6">
      {/* Logo / Branding */}
      <div className="text-center mb-10">
        <div className="text-5xl mb-3">🍽️</div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
          Sabbath Lunch
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
          Reset Your Password
        </p>
      </div>

      {/* Form Container */}
      <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-3xl shadow-sm border border-warm-100 dark:border-gray-800 p-6">
        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              autoComplete="email"
              required
            />

            {error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 px-3 py-2.5 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={loading}
              className="mt-6"
            >
              Send Reset Link
            </Button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Back to Login
              </button>
            </div>
          </form>
        ) : (
          <div className="text-center">
            <div className="text-5xl mb-4">✉️</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              Check Your Email
            </h2>
            {success && (
              <div className="rounded-xl bg-green-50 dark:bg-green-950 border border-green-100 dark:border-green-900 px-3 py-2.5 text-sm text-green-600 dark:text-green-400 mb-4">
                {success}
              </div>
            )}
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
              We've sent a password reset link to <strong>{email}</strong>. Click the link in the email to reset your password.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
